import { useQuery } from "@tanstack/react-query";
import { membershipApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type { VisitBalanceResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * A single subscription's visit balance (GET /memberships/visit-balances/{id} →
 * VisitBalanceResponse). Only meaningful for a visits_card plan; the caller
 * enables it only when the subscription is a pass. A non-pass subscription has
 * no balance (the read 404s) — the detail page treats that as "no pass balance"
 * rather than an error.
 */
export function useVisitBalance(
  subscriptionId: string | undefined,
  enabled = true,
) {
  const { user } = useAuth();
  return useQuery<VisitBalanceResponse>({
    queryKey: [...queryKeys.memberships.visitBalance(subscriptionId ?? ""), user?.uid],
    queryFn: ({ signal }) => membershipApi.getVisitBalance(subscriptionId as string, signal),
    enabled: Boolean(subscriptionId) && Boolean(user) && enabled,
    staleTime: 0,
    refetchOnMount: "always",
    // A pass with no balance yet (or a non-pass sub) 404s — don't hammer it.
    retry: false,
  });
}
